import { useRef, type ChangeEvent, type KeyboardEventHandler } from "react"
import { useDebounceCallback } from "usehooks-ts"
import { useHotkey, formatForDisplay } from "@tanstack/react-hotkeys"

import { cn } from "~/utils/helpers"

import { Input, type InputProps } from "../reusable/Input"
import { MagnifyingGlass } from "@phosphor-icons/react"
import { trackEvent } from "~/hooks/useTrackingService"
import { HOTKEYS } from "~/constants/hotkeys"
import { shortcutMeta } from "~/utils/shortcutCheatsheet"

type Props = {
  initialSearchText: string
  onSearchTextChange: (searchText: string) => void
  placeholder: string
  className?: string
  containerClassName?: string
  onKeyDown?: KeyboardEventHandler<HTMLInputElement> | undefined
  onInputPause?: (searchText: string) => void
  onBlur?: (searchText: string) => void
  showInfoElement?: boolean
  debounceTime?: number
  variant?: InputProps["variant"]
}

// TODO: We should add clearButton to this, but it needs some love to work.
function SearchField({
  initialSearchText,
  onSearchTextChange,
  placeholder,
  className,
  containerClassName = "w-60",
  onKeyDown,
  onInputPause,
  onBlur,
  showInfoElement = true,
  debounceTime = 500,
  variant = "default",
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)

  const handleChangeDebounced = useDebounceCallback((e: ChangeEvent<HTMLInputElement>) => {
    onInputPause?.(e.target.value)
    onSearchTextChange(e.target.value)
  }, debounceTime)

  useHotkey(
    HOTKEYS.focusSearchField.hotkey,
    () => {
      trackEvent("Search Field Focused", {
        Source: "Keyboard Shortcut",
        Shortcut: "Ctrl+F",
      })
      inputRef.current?.focus()
    },
    { meta: shortcutMeta("Tables", "Search table") },
  )

  return (
    <div className="relative flex items-center">
      <Input
        ref={inputRef}
        small
        variant={variant}
        defaultValue={initialSearchText}
        type="text"
        placeholder={placeholder}
        containerClassName={containerClassName}
        icon={
          <MagnifyingGlass
            size="16"
            className="absolute left-3 top-1/2 -translate-y-1/2 transform text-fg-muted"
          />
        }
        infoElement={
          showInfoElement ? (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 transform text-fg-muted text-sm">
              {formatForDisplay(HOTKEYS.focusSearchField.hotkey)}
            </div>
          ) : undefined
        }
        onKeyDown={onKeyDown}
        onClick={() => {
          trackEvent("Search Field Focused", {
            Source: "Click",
            Shortcut: "",
          })
        }}
        className={cn(
          "pl-8 shadow-none border border-strong",
          //initialSearchText.length > 0 && "border-hakio-green-dark", // Make it more clear that the search field is filled out.
          className,
        )}
        onChange={handleChangeDebounced}
        onBlur={onBlur ? (e) => onBlur(e.target.value) : undefined}
      />
    </div>
  )
}

export default SearchField
